import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ValueCounter from "@/components/ValueCounter";
import PartnerOfMonth from "@/components/PartnerOfMonth";
import ReferralCallout from "@/components/ReferralCallout";
import PhoneMockup from "@/components/PhoneMockup";
import Premium3DCard from "@/components/Premium3DCard";
import PartnerDetailDialog from "@/components/PartnerDetailDialog";
import PartnersMarquee from "@/components/PartnersMarquee";
import BentoPartnersHero from "@/components/BentoPartnersHero";
import { Input } from "@/components/ui/input";
import { useT } from "@/context/LangContext";
import { useBrand } from "@/context/BrandContext";
import {
  ArrowRight,
  ArrowDown,
  Search,
  Sparkles,
  MapPin,
  Store,
  Smartphone,
} from "lucide-react";

const APP_SCREEN = "/app-screen-v2.png";

export default function MembersLandingPage() {
  const { t, lang } = useT();
  const { brand } = useBrand();

  const [partners, setPartners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [cat, setCat] = useState("all");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, c, s] = await Promise.all([
          api.get("/partners"),
          api.get("/categories"),
          api.get("/settings/public").catch(() => ({ data: null })),
        ]);
        setPartners(p.data || []);
        setCategories(c.data || []);
        setSettings(s.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Live stats
  const stats = useMemo(() => {
    const monthly = partners
      .filter((p) => p.counts_in_counter !== false)
      .reduce((sum, p) => {
        const val = (p.offer_value || 0) * (p.monthly_estimated_uses || 1);
        return sum + Math.min(val, p.monthly_cap || 15);
      }, 0);
    return {
      partners: partners.length,
      monthly: Math.round(monthly),
      categories: categories.length,
    };
  }, [partners, categories]);

  // Category counts
  const catCounts = useMemo(() => {
    const map = { all: partners.length };
    categories.forEach((c) => {
      map[c] = partners.filter((p) => p.category === c).length;
    });
    return map;
  }, [partners, categories]);

  const filtered = useMemo(() => {
    return partners.filter((p) => {
      if (cat !== "all" && p.category !== cat) return false;
      if (search && !(p.name + " " + p.description + " " + p.offer).toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [partners, cat, search]);

  return (
    <div className="App bg-white" data-testid="members-landing">
      <Header />

      {/* Live scrolling strip of current partners — first thing visible on page load */}
      <PartnersMarquee />

      {/* ============ BENTO HERO (partner-first visual) ============ */}
      <BentoPartnersHero partners={partners} stats={stats} onOpenPartner={setActive} />

      {/* ============ PARTNERS GRID with live filters ============ */}
      <section
        id="partenaires"
        className="section-pad bg-white relative overflow-hidden"
        data-testid="mlp-partners-section"
      >
        <div
          className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-10 blur-3xl pointer-events-none"
          style={{ background: "var(--brand-accent, #BFFF00)" }}
        />
        <div className="container-app relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mb-10"
          >
            <div className="inline-block chip bg-lime-500/15 text-navy-900 font-bold uppercase tracking-widest text-[11px] mb-4">
              <Sparkles className="w-3 h-3" /> {t("mlp.partners.kicker")}
            </div>
            <h2 className="font-heading font-black text-navy-900 text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
              {t("mlp.partners.title")}
            </h2>
            <p className="mt-4 text-lg text-navy-900/60 leading-relaxed">
              {t("mlp.partners.subtitle")}
            </p>
          </motion.div>

          {/* SEARCH + CATEGORIES */}
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-10">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-navy-900/40" />
              <Input
                data-testid="mlp-search-input"
                placeholder={t("mlp.partners.search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-11 h-12 rounded-full border-navy-900/10 bg-white shadow-sm focus:shadow-md transition-shadow"
              />
            </div>
          </div>

          <div className="flex gap-2 overflow-x-auto no-scrollbar mb-8 pb-2">
            <CategoryChip
              active={cat === "all"}
              onClick={() => setCat("all")}
              label={t("mlp.partners.cat_all")}
              count={catCounts.all}
              testId="mlp-cat-all"
            />
            {categories.map((c) => (
              <CategoryChip
                key={c}
                active={cat === c}
                onClick={() => setCat(c)}
                label={c}
                count={catCounts[c] || 0}
                testId={`mlp-cat-${c}`}
              />
            ))}
          </div>

          {/* GRID */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="rounded-3xl bg-navy-900/5 animate-pulse aspect-[4/5]" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24 text-navy-900/50" data-testid="mlp-empty-state">
              <div className="text-4xl mb-3">{partners.length === 0 ? "🌱" : "🔍"}</div>
              <div className="text-lg text-navy-900/70 font-semibold">
                {partners.length === 0 ? t("mlp.partners.empty_soon") : t("mlp.partners.empty")}
              </div>
            </div>
          ) : (
            <motion.div
              layout
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              <AnimatePresence mode="popLayout">
                {filtered.map((p, i) => (
                  <motion.div key={p.id} layout>
                    <Premium3DCard partner={p} index={i} onClick={() => setActive(p)} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </section>

      {/* ============ VALUE COUNTER ============ */}
      <ValueCounter partners={partners} settings={settings} />

      {/* ============ REFERRAL ============ */}
      <ReferralCallout variant="b2c" />

      {/* ============ PARTNER OF MONTH ============ */}
      <PartnerOfMonth />

      {/* ============ MOBILE APP + CARD SECTION ============ */}
      <section className="section-pad bg-navy-900 text-white relative overflow-hidden" data-testid="mlp-app-section">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-3xl opacity-20 pointer-events-none"
             style={{ background: "var(--brand-accent, #BFFF00)" }} />
        <div className="container-app relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-6 order-2 lg:order-1"
            >
              <div className="inline-block chip bg-lime-500/20 text-lime-400 font-bold uppercase tracking-widest text-[11px] mb-4">
                <Smartphone className="w-3 h-3" /> {t("mlp.app.badge")}
              </div>
              <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tighter leading-[1.05] mb-5">
                {t("mlp.app.title")}
              </h2>
              <p className="text-white/70 text-lg leading-relaxed">
                {t("mlp.app.desc")}
              </p>
            </motion.div>
            <div className="lg:col-span-6 order-1 lg:order-2 flex justify-center">
              <PhoneMockup image={APP_SCREEN} />
            </div>
          </div>
        </div>
      </section>

      {/* ============ DISCREET PRO STRIP ============ */}
      <section className="py-8 bg-white border-t border-navy-900/5" data-testid="mlp-pro-strip">
        <div className="container-app flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
          <div className="flex items-center gap-2 text-sm text-navy-900/60">
            <Store className="w-4 h-4" />
            {t("mlp.pro_strip.text", { city: brand.city })}
          </div>
          <Link
            to="/pro"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-navy-900 hover:text-lime-600 transition-colors"
            data-testid="mlp-pro-link"
          >
            {t("mlp.pro_strip.cta")}
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>

      <Footer />

      <PartnerDetailDialog partner={active} open={!!active} onOpenChange={(v) => !v && setActive(null)} />
    </div>
  );
}

function CategoryChip({ active, onClick, label, count, testId }) {
  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      data-testid={testId}
      className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border transition-all whitespace-nowrap ${
        active
          ? "bg-navy-900 text-white border-navy-900 shadow-md"
          : "bg-white text-navy-900 border-navy-900/10 hover:border-navy-900/40 hover:shadow"
      }`}
    >
      <span>{label}</span>
      <span
        className={`text-[10px] font-black px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${
          active ? "bg-lime-400 text-navy-900" : "bg-navy-900/8 text-navy-900/60"
        }`}
      >
        {count}
      </span>
    </motion.button>
  );
}
